import { useAuth0 } from "@auth0/auth0-react";
import { ROUTES } from "@config";
import { useGetUser } from "@hooks";
import { Alert, Button } from "@mui/material";
import { captureSentryMessage, fillRouteWithSlugs } from "@utils";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { Loading } from "src/pages/Loading";
import { authHelper } from "src/stores/helpers";
import { User } from "src/stores/models/user";
import { getUserHomeOrg } from "src/utils/user";

const getReturnToLink = () => {
  // Check if there's a saved returnTo in sessionStorage
  const savedReturnTo = sessionStorage.getItem("returnTo");
  sessionStorage.removeItem("returnTo");
  return savedReturnTo;
};

/**
 * Decides where to send user before hitting protected route
 */
export const NavigateToHomeOrReturnTo = () => {
  const {
    isLoading,
    isAuthenticated,
    user: authUser,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: user, isError, refetch } = useGetUser(
    isAuthenticated && !!authUser
  );

  const navigateToReturnTo = (user: User) => {
    const returnTo = getReturnToLink();
    const currentLocation = `${location.pathname}${location.search}`;
    if (!returnTo || returnTo === currentLocation) return false;

    let navigated = false;
    user.organizationMemberships.forEach((orgMember) => {
      if (returnTo.includes(orgMember.organizationId)) {
        navigate(returnTo);
        navigated = true;
        return;
      }
    });
    return navigated;
  };

  useEffect(() => {
    if (!isLoading && user) {
      const hasNoOrgs = user.organizationMemberships.length === 0;
      if (hasNoOrgs) {
        navigate(fillRouteWithSlugs(ROUTES.DEACTIVATED_USER));
        return;
      }

      // Then try to navigate to return path or home
      if (navigateToReturnTo(user)) return;

      const homeOrgId = getUserHomeOrg(user)?.id ?? "";
      let firstAvailableOrgId = homeOrgId;

      if (!homeOrgId) {
        firstAvailableOrgId = user.organizationMemberships[0].organizationId;
      }

      navigate(
        fillRouteWithSlugs(ROUTES.DASHBOARD, {
          [ROUTE_SLUGS.ORG_ID]: firstAvailableOrgId,
        })
      );
    }
  }, [user, authUser, isAuthenticated, isLoading]);

  useEffect(() => {
    if (!isLoading && authUser && isAuthenticated) {
      authHelper.setAuth0Functions({
        loginWithRedirect,
        logout,
        user: authUser,
        isAuthenticated,
        getAccessTokenSilently,
      });
    }
  }, [isLoading, authUser, isAuthenticated, loginWithRedirect, logout]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoading) {
        captureSentryMessage("Navigation loading took more than 5 seconds", {
          level: "warning",
          tags: {
            component: "NavigateToHomeOrReturnTo",
          },
          captureContext: {
            isLoading,
            isAuthenticated,
            authUser,
          },
        });
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, []);

  if (isError) {
    return (
      <div className="flex flex-1 h-full items-center justify-center py-2 px-3">
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Unable to load your account. Please try again.
        </Alert>
      </div>
    );
  }

  return <Loading />;
};
